import type { StyledProps } from '@compiled/react';
import { styled } from '@compiled/react';
import type { MutableRefObject } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	injectIntl,
	useIntl,
	type IntlShape,
	type MessageDescriptor,
	type WithIntlProps,
} from 'react-intl';

import { cssMap } from '@atlaskit/css';
import type { ConflictChange } from '@atlaskit/editor-common/collab';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/useSharedPluginStateSelector';
import { isOfflineMode } from '@atlaskit/editor-plugin-connectivity/connectivityPluginType';
import { FULL_PAGE_EDITOR_TOOLBAR_HEIGHT } from '@atlaskit/editor-shared-styles/constants';
import MergeFailureIcon from '@atlaskit/icon/core/merge-failure';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import { type MAXIMUM_TWO_LINE_TOOLBAR_BREAKPOINT } from '@confluence/editor-common-styles';
import type { ConflictResolutionUIPlugin } from '@confluence/editor-presets/entry-points/presets/conflictResolutionUIPlugin.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { usePubSubEvent } from '@confluence/pubsub-client/entry-points/usePubSubEvent.ts';
import { useContentRestrictionSubscriptionEvent } from '@confluence/editor-features/entry-points/useContentRestrictionSubscriptionEvent.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { fg } from '@confluence/feature-gating';

import { AnimatedDot, EllipsisContainer } from './BrowserIndicators.tsx';
// TODO: Remove when platform_editor_offline_editing_web_ga_fixes is cleaned up - only used in old conflict message
import { CreateOfflineCopy } from './ConflictBanner/CreateOfflineCopy.tsx';
import type {
	EditorConnectionBannerAnalyticsType,
	EditorConnectionBannerTypeOffline,
	EditorConnectionBannerVariantDefinition,
	EditorConnectionBannerVariantInstance,
} from './EditorConnectionBannerTypes.ts';
import { i18n } from './messages.ts';
import { useDelayedBooleanState, useDelayedBooleanValue } from './useDelayedBooleanState.ts';

// Mirrored from the editor-presets package
type IdentifiableConflictChange = ConflictChange & { id: string };
type Conflicts = {
	inserted: IdentifiableConflictChange[];
	deleted: IdentifiableConflictChange[];
};
type AnyFunctionType = (...args: any[]) => any;

const AnimatedEllipsis = () => (
	<EllipsisContainer>
		<AnimatedDot />
		<AnimatedDot />
		<AnimatedDot />
	</EllipsisContainer>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StatusIndicatorOffline: React.ComponentType<
	{
		show: boolean;
		toolbarHidden?: boolean;
	} & React.ClassAttributes<HTMLDivElement> &
		React.HTMLAttributes<HTMLDivElement> &
		StyledProps
> =
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
	styled.div<{
		show: boolean;
		toolbarHidden?: boolean;
	}>({
		display: 'grid',
		position: 'absolute',
		width: '100%',
		justifyContent: 'center',
		alignItems: 'center',
		textAlign: 'center',
		// We are holding on converting FULL_PAGE_EDITOR_TOOLBAR_HEIGHT until active content topper development is complete
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		top: (props) => (props.toolbarHidden ? 0 : FULL_PAGE_EDITOR_TOOLBAR_HEIGHT()),
		left: token('space.0'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		transform: `scaleY(${(props) => (props.show ? 1 : 0)})`,
		transformOrigin: 'top',
		transition: 'all 300ms ease',
		zIndex: 400,
		['@media (max-width: 868px)' satisfies `@media (max-width: ${typeof MAXIMUM_TWO_LINE_TOOLBAR_BREAKPOINT}px)`]:
			{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				top: (props) => (props.toolbarHidden ? 0 : FULL_PAGE_EDITOR_TOOLBAR_HEIGHT()),
			},
	});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StyledText: React.ComponentType<
	React.ClassAttributes<HTMLSpanElement> & React.HTMLAttributes<HTMLSpanElement> & StyledProps
> =
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
	styled.span({
		fontFamily: 'inherit',
		font: token('font.body.small'),
		fontWeight: token('font.weight.bold'),
		color: token('color.text.subtlest'),
		whiteSpace: 'normal',
		overflow: 'hidden',
	});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FadingSpan = (props: any): React.JSX.Element => (
	<FadingSpanInternal aria-hidden={!props?.show} {...props} />
);
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const FadingSpanInternal = styled.span<{ show: boolean }>({
	display: 'flex',
	gap: token('space.100'),
	alignItems: 'center',
	justifyContent: 'center',
	marginLeft: token('space.100'),
	marginRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props) => (props.show ? 1 : 0),
	transition: 'opacity 0.5s',
	gridArea: '1 / 1',
});

const backgroundColor = cssMap({
	online: {
		backgroundColor: token('color.background.success'),
		transform: `scaleY(1)`,
		transition: 'none',
	},
	offline: { backgroundColor: token('color.background.accent.gray.subtlest') },
	conflicts: { backgroundColor: token('color.background.accent.gray.subtlest') },
});

const styles = cssMap({
	conflictLink: {
		backgroundColor: 'transparent',
		padding: '0',
		font: token('font.body.small'),
		fontWeight: token('font.weight.bold'),
		color: token('color.text.subtle'),
		borderBottomWidth: token('border.width'),
		borderBottomColor: token('color.border.accent.orange'),
		borderBottomStyle: 'dashed',
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.accent.orange.subtlest.hovered'),
		},
	},
	placeholder: {
		width: '14px',
	},
});

const isMessageDescriptor = (msg: MessageDescriptor | any): msg is MessageDescriptor => {
	return (
		!!msg &&
		typeof msg === 'object' &&
		(msg as MessageDescriptor).id !== undefined &&
		(msg as MessageDescriptor).defaultMessage !== undefined
	);
};

/**
 * Custom hook to manage the state of banner variants and provide a consistent interface for their usage.
 * @param variant - The variant definition to use. This should ideally be a constant defined outside of the component to prevent re-renders.
 * @param props - The props to pass to the variant condition function. This should be treated as a dependency array, and values should be kept in the same order.
 * @returns The current state of the banner variant.
 *
 * @privateRemarks
 *
 * Within this hook, the react-hooks/exhaustive-deps rule is disabled in a few places.
 * This is because we track the dependencies provided by the props argument as their values instead of the overall reference.
 * This is done to prevent the need to memoize the props object without causing unnecessary re-renders.
 * By directly depending on the values in this array, we can ensure that the hook will only re-run when the values change, not the array itself.
 *
 * This does however have the caveat that we lose static analysis of the dependencies, so extra care should be taken when working in this function.
 */
export function useBannerVariant<T extends Readonly<unknown[]>>(
	variant: EditorConnectionBannerVariantDefinition<T>,
	props: T,
	triggerAnalyticsEvent: AnyFunctionType,
): EditorConnectionBannerVariantInstance {
	const { condition: conditionFn, onConditionChange, onVisibilityChange, message } = variant;
	const [condition, setCondition] = useState<boolean>(conditionFn(...props));
	const { formatMessage } = useIntl();

	const lastConditionChange = useRef<number>(performance.now());
	const lastVisibilityChange = useRef<number>(performance.now());

	const fireConditionChange = useCallback(
		(newConditionValue: boolean) => {
			if (onConditionChange) {
				const timing = {
					startTime: lastConditionChange.current,
					endTime: performance.now(),
					duration: performance.now() - lastConditionChange.current,
				};
				lastConditionChange.current = performance.now();
				onConditionChange(newConditionValue, timing, triggerAnalyticsEvent, ...props);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Refer to private remarks in the function description
		[onConditionChange, triggerAnalyticsEvent, ...props],
	);

	useEffect(() => {
		const newConditionValue = conditionFn(...props);
		if (condition !== newConditionValue) {
			setCondition(newConditionValue);
			fireConditionChange(newConditionValue);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Refer to private remarks in the function description
	}, [conditionFn, onConditionChange, condition, triggerAnalyticsEvent, ...props]);

	// Fire condition change event on the first render to maintain consistency with previous behaviour for analytics
	const hasExecutedFirstRender = useRef<boolean>(false);
	useEffect(
		() => {
			if (!hasExecutedFirstRender.current) {
				hasExecutedFirstRender.current = true;
				fireConditionChange(condition);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Refer to private remarks in the function description
		[fireConditionChange, condition],
	);

	const wrappedOnVisibilityChange = useCallback(
		(visibilityValue: boolean) => {
			if (onVisibilityChange) {
				const timing = {
					startTime: lastVisibilityChange.current,
					endTime: performance.now(),
					duration: performance.now() - lastVisibilityChange.current,
				};
				lastVisibilityChange.current = performance.now();
				onVisibilityChange(visibilityValue, timing, triggerAnalyticsEvent, ...props);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Refer to private remarks in the function description
		[onVisibilityChange, triggerAnalyticsEvent, ...props],
	);

	const messageValue = useMemo<React.ReactNode>(() => {
		if (typeof message === 'function') {
			return message(...props);
		} else if (isMessageDescriptor(message)) {
			return formatMessage(message);
		}
		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Refer to private remarks in the function description
	}, [message, ...props]);

	return {
		id: variant.id,
		condition,
		message: messageValue,
		wrapperStyleKey: variant.wrapperStyleKey,
		onVisibilityChange: wrappedOnVisibilityChange,
	};
}

export const BackOnlineBannerDefinition: EditorConnectionBannerVariantDefinition<
	[isBackOnline: boolean]
> = {
	id: 'back-online-banner',
	condition: (isBackOnline) => isBackOnline,
	message: i18n.editorEnabledOfflineMode,
	wrapperStyleKey: 'online',
};

export const OfflineDisabledBannerDefinition: EditorConnectionBannerVariantDefinition<
	[isOfflineStorageUnavailable: boolean]
> = {
	id: 'offline-disabled-banner',
	condition: (isOfflineStorageUnavailable) => isOfflineStorageUnavailable,
	message: i18n.editorDisabled,
	wrapperStyleKey: 'offline',
};

export const OfflineBannerDefinition: EditorConnectionBannerVariantDefinition<
	[
		isOffline: boolean,
		offlineData: MutableRefObject<
			Omit<EditorConnectionBannerAnalyticsType, 'offlineStartTime' | 'connectionTimeoutStartTime'>
		>,
		editorType?: string,
		restrictionUpdate?: boolean,
	]
> = {
	id: 'offline-banner',
	condition: (isOffline) => isOffline,
	message: i18n.editorDisabledOfflineMode,
	wrapperStyleKey: 'offline',
	onConditionChange: (
		newCondition,
		_timing,
		triggerAnalyticsEvent,
		_isOffline,
		offlineData,
		editorType,
		restrictionUpdate,
	) => {
		if (newCondition) {
			// Save browser network status and document visibility state when the user goes offline
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			offlineData.current.browserNetworkStatus = navigator.onLine;
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			offlineData.current.documentVisibilityState = document.visibilityState;

			// Fire analytics event when the offline banner is now active (though it may not be visible yet)
			triggerAnalyticsEvent('offline.banner.show.indicator', {
				editorType,
				restrictionUpdate,
			});
		}
	},
	onVisibilityChange: (
		isVisible,
		timing,
		triggerAnalyticsEvent,
		_isOffline,
		onlineData,
		editorType,
		_restrictionUpdate,
	) => {
		if (!isVisible) {
			// Fire analytics event when the offline banner has been displayed
			triggerAnalyticsEvent('offline.banner.displayed', {
				browserNetworkStatus: onlineData.current.browserNetworkStatus,
				editorType,
				documentVisibilityState: onlineData.current.documentVisibilityState,
				duration: timing.duration,
			});
		}
	},
};

export const ConnectionTimeoutBannerDefinition: EditorConnectionBannerVariantDefinition<
	[
		connectionTimedOut: boolean,
		offlineData: MutableRefObject<
			Omit<EditorConnectionBannerAnalyticsType, 'offlineStartTime' | 'connectionTimeoutStartTime'>
		>,
		editorInfo: { contentId: string; contentType: string; editorType: string },
	]
> = {
	id: 'connection-timeout-banner',
	condition: (connectionTimedOut) => connectionTimedOut,
	message: i18n.collabEditConnectionTimeout,
	wrapperStyleKey: 'offline',
	onConditionChange: (
		newCondition,
		_timing,
		triggerAnalyticsEvent,
		_connectionTimedOut,
		offlineData,
		{ contentId, contentType },
	) => {
		if (newCondition) {
			// Save browser network status and document visibility state when the user goes offline
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			offlineData.current.browserNetworkStatus = navigator.onLine;
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			offlineData.current.documentVisibilityState = document.visibilityState;
			// Fire analytics event when the connection timeout banner is now active (though it may not be visible yet)
			triggerAnalyticsEvent('collab.edit.connection.timeout.show.indicator', {
				contentId,
				contentType,
			});
		}
	},
	onVisibilityChange: (
		isVisible,
		timing,
		triggerAnalyticsEvent,
		_connectionTimedOut,
		offlineData,
		{ editorType },
	) => {
		if (!isVisible) {
			// Fire analytics event when the connection timeout banner has been displayed
			triggerAnalyticsEvent('connection.timeout.banner.displayed', {
				browserNetworkStatus: offlineData.current.browserNetworkStatus,
				editorType,
				documentVisibilityState: offlineData.current.documentVisibilityState,
				duration: timing.duration,
			});
		}
	},
};

export const RestrictionUpdateBannerDefinition: EditorConnectionBannerVariantDefinition<
	[restrictionUpdate: boolean, willEditorDisable: boolean]
> = {
	id: 'restriction-update-banner',
	condition: (restrictionUpdate, willEditorDisable) => restrictionUpdate && willEditorDisable,
	message: i18n.editorDisabledDueToPermissionChange,
	wrapperStyleKey: 'offline',
};

export const ConflictBannerDefinition: EditorConnectionBannerVariantDefinition<
	[
		conflicts: Conflicts | undefined,
		formatMessage: IntlShape['formatMessage'],
		api: PublicPluginAPI<[ConflictResolutionUIPlugin]> | undefined,
		contentId: string,
		spaceId: string | undefined,
		setIsLoading: (loading: boolean) => void,
		isLivePage?: boolean,
	]
> = {
	id: 'conflict-banner',
	condition: (conflicts) =>
		conflicts !== undefined && (conflicts.deleted.length > 0 || conflicts.inserted.length > 0),
	wrapperStyleKey: 'conflicts',
	message: (
		conflicts,
		formatMessage,
		fullPageEditorPresetAPI,
		contentId,
		spaceId,
		setIsLoading,
		isLivePage,
	) => {
		const messageKey = fg('platform_editor_offline_editing_web_ga_fixes')
			? i18n.editorSyncFailedDueToConflictsUpdated
			: i18n.editorSyncFailedDueToConflicts;

		const conflictCount =
			conflicts !== undefined ? conflicts.deleted.length + conflicts.inserted.length : 0;

		let saveCopy: ((chunks: React.ReactNode) => React.ReactNode) | undefined;

		if (!fg('platform_editor_offline_editing_web_ga_fixes')) {
			saveCopy = (chunks) => (
				<CreateOfflineCopy
					api={fullPageEditorPresetAPI}
					chunks={chunks}
					contentId={contentId}
					spaceId={spaceId}
					setIsLoading={setIsLoading}
					isLivePage={isLivePage}
				/>
			);
		}

		return formatMessage(messageKey, {
			conflictIcon: <MergeFailureIcon label="" color={token('color.icon.accent.orange')} />,
			conflictLink: (chunks) => (
				<Pressable
					onClick={() => {
						if (fg('platform_editor_offline_editing_web_ga_fixes')) {
							fullPageEditorPresetAPI?.core?.actions.execute(
								fullPageEditorPresetAPI?.conflictResolutionUI?.commands.startResolvingConflicts,
							);
						} else {
							fullPageEditorPresetAPI?.core?.actions.execute(
								fullPageEditorPresetAPI?.conflictResolutionUI?.commands.navigateToFirstConflict,
							);
						}
					}}
					xcss={styles.conflictLink}
					testId="offline-conflict-navigate-to-first-conflict"
				>
					{chunks}
				</Pressable>
			),
			saveCopy,
			conflictCount,
		});
	},
};

export const ResyncingEditorDisabledBannerDefinition: EditorConnectionBannerVariantDefinition<
	[resyncingEditorDisabled: boolean]
> = {
	id: 'resyncing-editor-disabled-banner',
	condition: (resyncingEditorDisabled) => resyncingEditorDisabled,
	message: i18n.resyncingEditorDisabled,
	wrapperStyleKey: 'offline',
};

const EditorConnectionBannerComponent = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(
	injectIntl(
		({
			editorType,
			contentType,
			contentId,
			spaceId,
			triggerAnalyticsEvent,
			collabEditState,
			willEditorDisable = false,
			fullPageEditorPresetAPI,
			intl: { formatMessage },
			isLivePage,
			isEditorToolbarHidden,
		}: EditorConnectionBannerTypeOffline) => {
			const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

			// Timeout values for showing and hiding the banners
			const SHOW_TIMEOUT = 3000;
			const HIDE_TIMEOUT = 3000;

			// Track conflict state
			const conflicts = useSharedPluginStateSelector(
				fullPageEditorPresetAPI,
				'conflictResolutionUI.conflicts',
			);

			const isOfflineStorageDisabled = Boolean(
				useSharedPluginStateSelector(fullPageEditorPresetAPI, 'editorOfflineStorage.disabled'),
			);

			const offlineStorageIsInactiveSyncing =
				useSharedPluginStateSelector(
					fullPageEditorPresetAPI,
					'editorOfflineStorage.inactiveSyncing',
				) ?? false;

			const connectivityMode = useSharedPluginStateSelector(
				fullPageEditorPresetAPI,
				'connectivity.mode',
			);
			const internetLost = connectivityMode !== undefined && isOfflineMode(connectivityMode);

			// Track if the user is online
			const hasInternet = useDelayedBooleanValue(
				isFirstRender ? true : !(internetLost && willEditorDisable),
				SHOW_TIMEOUT,
				HIDE_TIMEOUT,
			);
			const connectionTimedOut = useDelayedBooleanValue(
				isFirstRender ? false : collabEditState.collabEditConnectionTimedOut && willEditorDisable,
				SHOW_TIMEOUT,
				HIDE_TIMEOUT,
			);

			const isOfflineStorageUnavailable = useDelayedBooleanValue(
				isFirstRender ? false : isOfflineStorageDisabled && !willEditorDisable && internetLost,
				0,
				0,
			);

			const isOnline = !isOfflineStorageDisabled
				? hasInternet && !connectionTimedOut
				: !internetLost && !connectionTimedOut;

			// Track when the user goes back online
			const [isBackOnline, setIsBackOnline] = useState<boolean>(false);
			const lastOnlineState = useRef<boolean>(isOnline);
			const backOnlineBannerTimeout = useRef<number | null>(null);
			const SHOW_BACK_ONLINE_TIMEOUT = 3000; // Show the back online banner for 3 seconds
			const [isLoading, setIsLoading] = useState(false);

			// Track restriction updates (permission changes)
			const [restrictionUpdate, setRestrictionUpdate] = useDelayedBooleanState(
				false,
				0, // Restriction updates should be shown immediately
				HIDE_TIMEOUT,
			);

			const onRestrictionUpdatedEvent = useCallback(() => {
				triggerAnalyticsEvent('offline.banner.restriction.updated', {
					editorType,
					restrictionUpdate,
				});
				if (editorType === 'classic') {
					setRestrictionUpdate(true);
				}
			}, [editorType, restrictionUpdate, setRestrictionUpdate, triggerAnalyticsEvent]);

			const onRestrictionUpdatedPubSubEvent = useCallback(() => {
				if (
					!['DUAL', 'GRAPHQL'].includes(
						StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
					) ||
					!expVal('cc_fps_migration_content-restriction-updated', 'isEnabled', false)
				) {
					onRestrictionUpdatedEvent();
				}
			}, [onRestrictionUpdatedEvent]);

			// Listen for restriction update events (permission changes)
			usePubSubEvent({
				contentId,
				contentType,
				eventName: `restrictionUpdated:${contentType}`,
				onEvent: onRestrictionUpdatedPubSubEvent,
				skip:
					// eslint-disable-next-line confluence-feature-gating/no-preconditioning
					expVal('cc_fps_migration_content-restriction-updated', 'isEnabled', false) &&
					fg('confluence_enable_subscriptions-context-provider') &&
					StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
						'GRAPHQL',
			});

			useContentRestrictionSubscriptionEvent({
				contentId,
				contentType,
				callback: onRestrictionUpdatedEvent,
				subscriberId: 'EditorConnectionBannerOfflineModeSubscription',
			});

			// Reset restriction update when editor reconnects so the default message is shown next time the editor goes offline
			useEffect(() => {
				if (!willEditorDisable) {
					setRestrictionUpdate(false);
				}
			}, [setRestrictionUpdate, willEditorDisable]);

			useEffect(() => {
				// If the user has gone back online after being offline, show the banner for a short time
				if (!lastOnlineState.current && isOnline) {
					setIsBackOnline(true);
					// Clear any existing timeout to avoid hiding the banner too early
					if (backOnlineBannerTimeout.current) {
						clearTimeout(backOnlineBannerTimeout.current);
					}
					backOnlineBannerTimeout.current = window.setTimeout(() => {
						setIsBackOnline(false);
						backOnlineBannerTimeout.current = null;
					}, SHOW_BACK_ONLINE_TIMEOUT);
				} else if (lastOnlineState.current && !isOnline) {
					setIsBackOnline(false);
					// Strictly speaking this isn't necessary, the callback now has no effect, but I figure it's good to clean the timeout up anyway
					if (backOnlineBannerTimeout.current) {
						clearTimeout(backOnlineBannerTimeout.current);
					}
				}
				lastOnlineState.current = isOnline;
			}, [isOnline, setIsBackOnline]);

			// Create the banner variant states and manage their visibility
			const backOnlineBanner = useBannerVariant(
				BackOnlineBannerDefinition,
				[isBackOnline],
				triggerAnalyticsEvent,
			);
			const offlineData = useRef<
				Omit<EditorConnectionBannerAnalyticsType, 'offlineStartTime' | 'connectionTimeoutStartTime'>
			>({
				browserNetworkStatus: true,
				documentVisibilityState: '',
			});
			const offlineBanner = useBannerVariant(
				OfflineBannerDefinition,
				[!hasInternet, offlineData, editorType, restrictionUpdate],
				triggerAnalyticsEvent,
			);
			// Use a memoized editor info object to avoid unnecessary re-renders
			const editorInfo = useMemo(
				() => ({ contentId, contentType, editorType }),
				[contentId, contentType, editorType],
			);
			const connectionTimeoutBanner = useBannerVariant(
				ConnectionTimeoutBannerDefinition,
				[connectionTimedOut, offlineData, editorInfo],
				triggerAnalyticsEvent,
			);
			const restrictionUpdateBanner = useBannerVariant(
				RestrictionUpdateBannerDefinition,
				[restrictionUpdate, willEditorDisable],
				triggerAnalyticsEvent,
			);
			const disabledOfflineBanner = useBannerVariant(
				OfflineDisabledBannerDefinition,
				[isOfflineStorageUnavailable],
				triggerAnalyticsEvent,
			);
			const conflictBanner = useBannerVariant(
				ConflictBannerDefinition,
				[
					conflicts,
					formatMessage,
					fullPageEditorPresetAPI,
					contentId,
					spaceId,
					setIsLoading,
					isLivePage,
				],
				triggerAnalyticsEvent,
			);

			const resyncingEditorDisabledBanner = useBannerVariant(
				ResyncingEditorDisabledBannerDefinition,
				[offlineStorageIsInactiveSyncing],
				triggerAnalyticsEvent,
			);

			// Banner list is ordered by priority, so the first valid banner will be shown
			const banners = [
				conflictBanner,
				disabledOfflineBanner,
				resyncingEditorDisabledBanner,
				backOnlineBanner,
				restrictionUpdateBanner,
				offlineBanner,
				connectionTimeoutBanner,
			];

			// Track the most recent banner to detect changes in visibility (separately from tracking if each banner's condition has changed)
			const lastBanner = useRef<string | undefined>(undefined);
			const currentBanner = banners.find((banner) => banner.condition);
			if (currentBanner?.id !== lastBanner.current) {
				// Fire a visibility change event for the banner that was previously visible
				if (lastBanner.current !== undefined) {
					// We can assume that the last ID is in the list of banners, as the banner list is constant, so we'll mark the result type as non-null
					const lastBannerInstance = banners.find((banner) => banner.id === lastBanner.current)!;
					if (lastBannerInstance.onVisibilityChange !== undefined) {
						lastBannerInstance.onVisibilityChange(false);
					}
				}
				// Fire a visibility change event for the banner that is now visible (if any)
				if (currentBanner?.onVisibilityChange !== undefined) {
					currentBanner.onVisibilityChange(true);
				}
				lastBanner.current = currentBanner?.id;
			}

			useEffect(() => {
				if (isFirstRender) {
					setIsFirstRender(false);
				}
			}, [isFirstRender]);

			return (
				<EditorCurrentBannerComponent
					isLoading={isLoading}
					currentBanner={currentBanner}
					isEditorToolbarHidden={isEditorToolbarHidden}
				/>
			);
		},
	),
);

export const EditorCurrentBannerComponent = ({
	currentBanner,
	isLoading,
	isEditorToolbarHidden,
}: {
	currentBanner: EditorConnectionBannerVariantInstance | undefined;
	isLoading?: boolean;
	isEditorToolbarHidden?: boolean;
}): React.JSX.Element => {
	// Get the message to display in the banner (if any) and format it if necessary
	const CurrentMessage = useMemo(() => currentBanner?.message, [currentBanner]);

	const LoadingIndicator = useMemo(() => {
		if (currentBanner?.wrapperStyleKey !== 'conflicts') {
			return null;
		}
		// Show loading ellipsis for conflicts only for now
		// Show a placeholder when not loading to avoid layout shift
		return isLoading ? <AnimatedEllipsis /> : <span css={styles.placeholder} />;
	}, [isLoading, currentBanner]);

	return (
		<StatusIndicatorOffline
			data-testid="status-indicator"
			show={currentBanner !== undefined}
			toolbarHidden={isEditorToolbarHidden}
			css={[
				currentBanner?.wrapperStyleKey === 'online' && backgroundColor['online'],
				currentBanner?.wrapperStyleKey === 'offline' && backgroundColor['offline'],
				currentBanner?.wrapperStyleKey === 'conflicts' && backgroundColor['conflicts'],
			]}
		>
			<FadingSpan
				data-testid="status-indicator-text"
				role="alert"
				show={currentBanner !== undefined}
			>
				<StyledText data-testid="status-text">{CurrentMessage}</StyledText>
				{LoadingIndicator}
			</FadingSpan>
		</StatusIndicatorOffline>
	);
};

export const EditorConnectionBannerOfflineMode: React.NamedExoticComponent<
	WithIntlProps<EditorConnectionBannerTypeOffline>
> = React.memo(EditorConnectionBannerComponent);
